import { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { Spinner, Button, Input, StatCard } from '../components/common';

interface ErrorLog {
  id: number;
  error_class: string;
  message: string;
  source: string | null;
  email_sent: boolean;
  created_at: string;
  backtrace?: string;
  local_backtrace?: string[];
  context?: Record<string, unknown>;
  ai_prompt_text?: string;
  github_issue_url?: string | null;
}

interface ErrorLogsStats {
  total: number;
  today: number;
  unsent: number;
  unique_errors: number;
}

interface Pagination {
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

interface ErrorLogsData {
  errors: ErrorLog[];
  stats: ErrorLogsStats;
  sources: string[];
  error_classes: string[];
  pagination: Pagination;
}

interface ErrorLogDetailData {
  error: ErrorLog;
}

export default function ErrorLogsPage() {
  const { t } = useTranslation();
  const { user, isLoading, token } = useAuth();

  const [logsData, setLogsData] = useState<ErrorLogsData | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState<ErrorLog | null>(null);
  const [logDetailLoading, setLogDetailLoading] = useState(false);
  const [filterSource, setFilterSource] = useState<string>('');
  const [filterErrorClass, setFilterErrorClass] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch error logs
  const fetchLogs = useCallback(
    async (pageNum = 1) => {
      if (!user?.admin) return;

      setLogsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.append('page', pageNum.toString());
        if (filterSource) params.append('source', filterSource);
        if (filterErrorClass) params.append('error_class', filterErrorClass);
        if (searchQuery) params.append('search', searchQuery);

        const response = await fetch(`/admin/error_logs?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data: ErrorLogsData = await response.json();
        setLogsData(data);
        setPage(pageNum);
        // Clear selection when fetching new data
        setSelectedIds(new Set());
      } catch (err) {
        console.error('Failed to fetch error logs:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch error logs');
      } finally {
        setLogsLoading(false);
      }
    },
    [user?.admin, token, filterSource, filterErrorClass, searchQuery]
  );

  // Fetch single log detail
  const fetchLogDetail = async (logId: number) => {
    setLogDetailLoading(true);
    try {
      const response = await fetch(`/admin/error_logs/${logId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: ErrorLogDetailData = await response.json();
      setSelectedLog(data.error);
    } catch (err) {
      console.error('Failed to fetch log detail:', err);
    } finally {
      setLogDetailLoading(false);
    }
  };

  // Fetch logs when filters change
  useEffect(() => {
    if (user?.admin) {
      fetchLogs(1);
    }
  }, [user?.admin, filterSource, filterErrorClass, searchQuery]);

  // Copy AI prompt to clipboard
  const copyAiPrompt = async () => {
    if (selectedLog?.ai_prompt_text) {
      await navigator.clipboard.writeText(selectedLog.ai_prompt_text);
    }
  };

  // Handle search submit
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  // Bulk selection helpers
  const allSelected = useMemo(() => {
    if (!logsData?.errors?.length) return false;
    return logsData.errors.every((log) => selectedIds.has(log.id));
  }, [logsData?.errors, selectedIds]);

  const someSelected = useMemo(() => {
    return selectedIds.size > 0;
  }, [selectedIds]);

  const toggleSelectAll = () => {
    if (!logsData?.errors) return;
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(logsData.errors.map((log) => log.id)));
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    setIsDeleting(true);
    try {
      const response = await fetch('/admin/error_logs/bulk_destroy', {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      setShowDeleteConfirm(false);
      setSelectedIds(new Set());
      fetchLogs(page);
    } catch (err) {
      console.error('Failed to delete error logs:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete error logs');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // Redirect non-admin users
  if (!user?.admin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/admin"
          className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 mb-4"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t('admin.title')}
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('admin.errorLogs')}
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">{t('admin.errorLogsDescription')}</p>
      </div>

      {/* Stats Cards */}
      {logsData?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            value={logsData.stats.total.toLocaleString()}
            label={t('admin.errorLogsTotal')}
          />
          <StatCard
            value={logsData.stats.today.toLocaleString()}
            label={t('admin.errorLogsToday')}
            color="red"
          />
          <StatCard
            value={logsData.stats.unsent.toLocaleString()}
            label={t('admin.errorLogsUnsent')}
            color="amber"
          />
          <StatCard
            value={logsData.stats.unique_errors.toLocaleString()}
            label={t('admin.errorLogsUnique')}
            color="purple"
          />
        </div>
      )}

      {/* Error Detail View */}
      {selectedLog && (
        <div className="mb-6 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setSelectedLog(null)}
            className="mb-4 text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t('admin.backToLogs')}
          </button>

          {logDetailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Error header */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-red-600 dark:text-red-400">
                    {selectedLog.error_class}
                  </h2>
                  <p className="mt-1 text-gray-700 dark:text-gray-300">{selectedLog.message}</p>
                </div>
                <div className="flex gap-2">
                  {selectedLog.github_issue_url && (
                    <a
                      href={selectedLog.github_issue_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 text-sm bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
                      </svg>
                      {t('admin.createGitHubIssue')}
                    </a>
                  )}
                  <button
                    onClick={copyAiPrompt}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    {t('admin.copyForAI')}
                  </button>
                </div>
              </div>

              {/* Metadata */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.errorSource')}
                  </div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {selectedLog.source || '-'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.errorEmailSent')}
                  </div>
                  <div
                    className={`font-medium ${selectedLog.email_sent ? 'text-green-600' : 'text-amber-600'}`}
                  >
                    {selectedLog.email_sent ? t('common.yes') : t('common.no')}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.createdAt')}
                  </div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {new Date(selectedLog.created_at).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Context */}
              {selectedLog.context && Object.keys(selectedLog.context).length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    {t('admin.errorContext')}
                  </div>
                  <pre className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap break-words font-mono">
                    {JSON.stringify(selectedLog.context, null, 2)}
                  </pre>
                </div>
              )}

              {/* Local Backtrace */}
              {selectedLog.local_backtrace && selectedLog.local_backtrace.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    {t('admin.errorLocalBacktrace')}
                  </div>
                  <pre className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-words font-mono text-red-600 dark:text-red-400">
                    {selectedLog.local_backtrace.join('\n')}
                  </pre>
                </div>
              )}

              {/* Full Backtrace */}
              {selectedLog.backtrace && (
                <details className="group">
                  <summary className="text-sm font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200">
                    {t('admin.errorFullBacktrace')}
                  </summary>
                  <pre className="mt-2 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap break-words font-mono text-gray-600 dark:text-gray-400">
                    {selectedLog.backtrace}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
      )}

      {/* Search and Filters */}
      {!selectedLog && (
        <div className="space-y-4 mb-6">
          {/* Search bar */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 max-w-md">
              <Input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('admin.searchErrorsPlaceholder')}
                className="w-full"
              />
            </div>
            <Button type="submit" variant="secondary" disabled={logsLoading}>
              {t('admin.search')}
            </Button>
            {searchQuery && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setSearchInput('');
                  setSearchQuery('');
                }}
              >
                {t('common.clear')}
              </Button>
            )}
          </form>

          {/* Filters row */}
          <div className="flex flex-wrap gap-4">
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('admin.allSources')}</option>
              {logsData?.sources?.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>

            <select
              value={filterErrorClass}
              onChange={(e) => setFilterErrorClass(e.target.value)}
              className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('admin.allErrorClasses')}</option>
              {logsData?.error_classes?.map((errorClass) => (
                <option key={errorClass} value={errorClass}>
                  {errorClass}
                </option>
              ))}
            </select>

            {/* Clear filters button */}
            {(filterSource || filterErrorClass) && (
              <button
                onClick={() => {
                  setFilterSource('');
                  setFilterErrorClass('');
                }}
                className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                {t('common.clear')}
              </button>
            )}
          </div>

          {/* Bulk actions bar */}
          {someSelected && (
            <div className="flex items-center gap-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                {t('admin.selectedCount', { count: selectedIds.size })}
              </span>
              <div className="flex gap-2">
                <Button variant="danger" size="sm" onClick={() => setShowDeleteConfirm(true)}>
                  {t('admin.deleteSelected')}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setSelectedIds(new Set())}>
                  {t('admin.clearSelection')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Logs Table */}
      {!selectedLog && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {logsLoading && !logsData ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : logsData?.errors?.length ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('admin.errorClass')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('admin.errorMessage')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('admin.errorSource')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('admin.createdAt')}
                      </th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {logsData.errors.map((log) => (
                      <tr
                        key={log.id}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                          selectedIds.has(log.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(log.id)}
                            onChange={() => toggleSelect(log.id)}
                            className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs font-medium">
                            {log.error_class}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 max-w-md truncate">
                          {log.message}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {log.source || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => fetchLogDetail(log.id)}
                            className="text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {t('admin.viewDetails')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {logsData.pagination.total_pages > 1 && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.showingPage', {
                      page: logsData.pagination.page,
                      total: logsData.pagination.total_pages,
                    })}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => fetchLogs(page - 1)}
                      disabled={page <= 1 || logsLoading}
                    >
                      {t('admin.previousPage')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => fetchLogs(page + 1)}
                      disabled={page >= logsData.pagination.total_pages || logsLoading}
                    >
                      {t('admin.nextPage')}
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              {t('admin.noErrorLogs')}
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {t('admin.confirmDeleteTitle')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('admin.confirmDeleteMessage', { count: selectedIds.size })}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                {t('common.cancel')}
              </Button>
              <Button variant="danger" onClick={handleBulkDelete} disabled={isDeleting}>
                {isDeleting ? t('common.loading') : t('common.delete')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
